import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowUpRight, Check, Clock, Loader2, Lock, QrCode, RefreshCw, Smartphone, X,
} from 'lucide-react';
import { LogoMark, apiUrl } from '@riskpay/ui-components';

/* ==========================================================================
 *  Hosted checkout
 *
 *  The only page on the platform a member of the public ever sees. It is
 *  written to a different brief from the consoles:
 *
 *    · one decision per screen — the customer is here to pay, not to browse;
 *    · the amount and who is being paid are legible before anything else,
 *      because that is what a person checks before parting with money;
 *    · every state is named in plain words. "Failed" with no explanation is
 *      how a customer becomes a support ticket;
 *    · the countdown is honest. A session that has expired says so rather
 *      than failing at the moment of payment.
 *
 *  The URL token is the only credential. There is no login, no cookie, and
 *  nothing here is cached anywhere it could outlive the session.
 * ========================================================================== */

interface Session {
  session_id: string;
  status: 'created' | 'opened' | 'processing' | 'paid' | 'failed' | 'expired' | 'cancelled';
  mode: 'test' | 'live';
  amount_formatted: string;
  currency: string;
  description: string | null;
  order_id: string | null;
  customer: { name: string | null; email: string | null; phone: string | null };
  merchant: { name: string; legal_name: string | null; support_email: string | null; website: string | null };
  seconds_remaining: number;
}

interface PayResult {
  type: 'redirect' | 'upi_intent' | 'done';
  payment_id?: string;
  payment_url?: string;
  intent_uri?: string;
  qr_url?: string;
  apps?: { key: string; name: string; link: string }[];
  redirect_url?: string;
  status?: string;
}

interface StatusResult {
  status: Session['status'];
  payment_status: string | null;
  payment_id: string | null;
  message: string | null;
  seconds_remaining: number;
  redirect_url: string | null;
}

const token = window.location.pathname.replace(/^\/c\//, '').replace(/\/$/, '');
const base = apiUrl(`/api/v1/checkout/${encodeURIComponent(token)}`);

async function call<T>(path: string, method: 'GET' | 'POST' = 'GET'): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(method === 'POST' ? { body: '{}' } : {}),
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(body?.error?.message ?? 'Something went wrong. Please try again.');
  }
  return body as T;
}

function useCountdown(seconds: number | undefined) {
  const [left, setLeft] = useState(seconds ?? 0);

  useEffect(() => {
    if (seconds === undefined) return;
    setLeft(seconds);
  }, [seconds]);

  useEffect(() => {
    if (left <= 0) return;
    const timer = window.setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [left > 0]);

  const label = useMemo(() => {
    const m = Math.floor(left / 60);
    const s = left % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [left]);

  return { left, label };
}

/** Coarse device test. Wrong on a touchscreen laptop, and harmless there — it
 *  only decides whether the QR or the app buttons come first. */
const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [pay, setPay] = useState<PayResult | null>(null);
  const [status, setStatus] = useState<StatusResult | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const redirected = useRef(false);

  const { left, label } = useCountdown(session?.seconds_remaining);

  useEffect(() => {
    call<Session>('')
      .then(setSession)
      .catch((err: Error) => setLoadError(err.message));
  }, []);

  /*
   * Read the status once on load, whatever state the page is in.
   *
   * A customer coming back from the gateway arrives here on a fresh page load,
   * so `pay` is null — it went away with the navigation. The poll below is
   * gated on `pay`, and the hand-back to the merchant is driven by
   * `status.redirect_url`, so without this single fetch a paid customer lands
   * on a finished payment and sits there: no redirect, no way onward, nothing
   * to click. Which is exactly what happened.
   *
   * Nothing renders until this has answered — see the gate further down. A page
   * that paints the Pay button first and corrects itself a moment later shows
   * someone who has already paid a button asking them to pay again.
   */
  useEffect(() => {
    call<StatusResult>('/status')
      .then(setStatus)
      .catch(() => undefined)
      .finally(() => setStatusLoaded(true));
  }, []);

  const state = status?.status ?? session?.status;
  const terminal = state === 'paid' || state === 'failed' || state === 'expired' || state === 'cancelled';

  // Poll only while a payment is genuinely in flight. Polling a page nobody has
  // started is noise, and polling a finished one is worse: the redirect below
  // fires from the same state.
  useEffect(() => {
    if (!pay || terminal) return;
    const timer = window.setInterval(() => {
      call<StatusResult>('/status').then(setStatus).catch(() => undefined);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [pay, terminal]);

  // Hand the customer back to the merchant once, on a terminal state.
  useEffect(() => {
    if (!status?.redirect_url || redirected.current) return;
    redirected.current = true;
    const timer = window.setTimeout(() => {
      // replace, not assign: going back should not land on a finished
      // checkout that still shows a Pay button.
      window.location.replace(status.redirect_url!);
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [status?.redirect_url]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await call<PayResult>('/pay', 'POST');
      setPay(result);
      if (result.type === 'redirect' && result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }
      if (result.type === 'done' && result.redirect_url) {
        window.location.href = result.redirect_url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the payment.');
    } finally {
      setBusy(false);
    }
  }, []);

  const cancel = useCallback(async () => {
    try {
      const result = await call<{ redirect_url: string }>('/cancel', 'POST');
      window.location.href = result.redirect_url;
    } catch {
      setError('Could not cancel. Close this window to abandon the payment.');
    }
  }, []);

  /* ---- Chrome ---------------------------------------------------------- */

  const frame = (children: React.ReactNode) => (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-8">
      <main className="w-full max-w-[400px]">{children}</main>
      <footer className="mt-6 flex items-center gap-1.5 text-2xs text-content-faint">
        <Lock className="h-3 w-3" aria-hidden="true" />
        Secured by
        <LogoMark size={20} />
        <span className="font-medium text-content-subtle">RiskPay</span>
      </footer>
    </div>
  );

  if (loadError) {
    return frame(
      <div className="rounded-xl border border-line bg-surface-raised p-7 text-center">
        <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-danger-ring bg-danger-soft">
          <AlertTriangle className="h-4 w-4 text-danger-text" aria-hidden="true" />
        </span>
        <h1 className="mt-4 text-md font-semibold tracking-tight">This payment link is not valid</h1>
        <p className="mt-2 text-sm leading-relaxed text-content-muted">
          It may have already been used, or it expired. Ask the merchant for a fresh link — nothing has been charged.
        </p>
      </div>,
    );
  }

  /*
   * Wait for both. The session alone is not enough to know what to draw: a
   * customer returning from the gateway would be shown the Pay screen for the
   * moment it takes the status to arrive, and pressing back would put them
   * right back on it.
   */
  if (!session || !statusLoaded) {
    return frame(
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-content-faint" aria-hidden="true" />
      </div>,
    );
  }

  /* ---- Terminal states -------------------------------------------------- */

  if (terminal) {
    const paid = state === 'paid';
    const copy: Record<string, { title: string; body: string }> = {
      paid: { title: 'Payment successful', body: 'Your payment is confirmed. Taking you back to the merchant.' },
      failed: {
        title: 'Payment did not go through',
        body: status?.message ?? 'The payment was declined. Nothing has been charged — you can try again from the merchant.',
      },
      expired: {
        title: 'This checkout expired',
        body: 'The session timed out before the payment completed. Nothing has been charged.',
      },
      cancelled: { title: 'Payment cancelled', body: 'Nothing has been charged.' },
    };
    const { title, body } = copy[state as string] ?? copy.failed!;

    return frame(
      <div className="rounded-xl border border-line bg-surface-raised p-7 text-center">
        <span
          className={`mx-auto flex h-11 w-11 items-center justify-center rounded-full border ${
            paid ? 'border-brand-700 bg-brand-soft' : 'border-line bg-surface-inset'
          }`}
        >
          {paid ? (
            <Check className="h-5 w-5 text-brand-400" aria-hidden="true" />
          ) : (
            <X className="h-5 w-5 text-content-subtle" aria-hidden="true" />
          )}
        </span>

        <h1 className="mt-4 text-lg font-semibold tracking-tight">{title}</h1>
        <p className="mx-auto mt-2 max-w-[30ch] text-sm leading-relaxed text-content-muted">{body}</p>

        <p className="numeral mt-5 border-t border-line pt-4 text-xl font-semibold tracking-tight">
          {session.amount_formatted}
        </p>
        <p className="mt-1 text-xs text-content-subtle">{session.merchant.name}</p>
        {status?.payment_id && (
          <p className="numeral mt-3 text-2xs text-content-faint">{status.payment_id}</p>
        )}

        {status?.redirect_url && (
          <a href={status.redirect_url} className="btn btn-secondary btn-sm mt-5 w-full">
            Return to {session.merchant.name}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>,
    );
  }

  /* ---- UPI intent ------------------------------------------------------- */

  if (pay?.type === 'upi_intent') {
    return frame(
      <div className="overflow-hidden rounded-xl border border-line bg-surface-raised">
        <header className="border-b border-line px-6 py-5 text-center">
          <p className="text-xs text-content-subtle">{session.merchant.name}</p>
          <p className="numeral mt-1 text-2xl font-semibold tracking-tight">{session.amount_formatted}</p>
        </header>

        <div className="p-6">
          {isMobile ? (
            <>
              <p className="flex items-center gap-2 text-sm font-medium">
                <Smartphone className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
                Pay with any UPI app
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {(pay.apps ?? []).slice(0, 6).map((app) => (
                  <a key={app.key} href={app.link} className="btn btn-secondary justify-center">
                    {app.name}
                  </a>
                ))}
              </div>
              <a href={pay.intent_uri} className="btn btn-primary btn-lg mt-3 w-full">
                Open my UPI app
              </a>
              {pay.qr_url && (
                <details className="mt-5 border-t border-line pt-4">
                  <summary className="cursor-pointer text-xs text-content-muted">Show QR instead</summary>
                  <img
                    src={pay.qr_url}
                    alt="Scan to pay with a UPI app"
                    width={220}
                    height={220}
                    className="mx-auto mt-4 rounded-lg bg-white p-2"
                  />
                </details>
              )}
            </>
          ) : (
            <>
              <p className="flex items-center justify-center gap-2 text-sm font-medium">
                <QrCode className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
                Scan with any UPI app
              </p>
              {pay.qr_url && (
                <img
                  src={pay.qr_url}
                  alt="Scan to pay with a UPI app"
                  width={232}
                  height={232}
                  className="mx-auto mt-4 rounded-lg bg-white p-2.5"
                />
              )}
              <p className="mt-4 text-center text-xs leading-relaxed text-content-subtle">
                Open Google Pay, PhonePe, Paytm or your bank's app, scan this code, and approve the payment. This page
                updates on its own.
              </p>
            </>
          )}

          <div className="mt-6 flex items-center justify-between gap-3 border-t border-line pt-4">
            <p className="flex items-center gap-1.5 text-2xs text-content-faint">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Waiting for your approval
            </p>
            <p className="numeral flex items-center gap-1.5 text-2xs text-content-faint">
              <Clock className="h-3 w-3" aria-hidden="true" />
              {label}
            </p>
          </div>
        </div>
      </div>,
    );
  }

  /* ---- The pay screen --------------------------------------------------- */

  return frame(
    <div className="overflow-hidden rounded-xl border border-line bg-surface-raised">
      {session.mode === 'test' && (
        <p className="border-b border-warning-ring bg-warning-soft px-6 py-2 text-center text-2xs uppercase tracking-wider text-[#FCD34D]">
          Test mode · no real money moves
        </p>
      )}

      <header className="border-b border-line px-6 pb-6 pt-7 text-center">
        <p className="text-sm text-content-muted">Paying</p>
        <p className="mt-1 text-md font-medium tracking-tight text-content">{session.merchant.name}</p>
        <p className="numeral mt-4 text-[2rem] font-semibold leading-none tracking-tight">{session.amount_formatted}</p>
        {session.description && (
          <p className="mx-auto mt-3 max-w-[32ch] text-sm leading-relaxed text-content-subtle">{session.description}</p>
        )}
      </header>

      <dl className="divide-y divide-line-subtle px-6 text-xs">
        {session.order_id && (
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-content-faint">Order</dt>
            <dd className="numeral truncate text-content-muted">{session.order_id}</dd>
          </div>
        )}
        {session.customer.email && (
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-content-faint">Email</dt>
            <dd className="truncate text-content-muted">{session.customer.email}</dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-4 py-2.5">
          <dt className="text-content-faint">Expires in</dt>
          <dd className={`numeral ${left < 120 ? 'text-[#FCD34D]' : 'text-content-muted'}`}>{label}</dd>
        </div>
      </dl>

      <div className="p-6 pt-5">
        {error && (
          <p role="alert" className="mb-4 rounded-md border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
            {error}
          </p>
        )}

        <button type="button" onClick={start} disabled={busy || left <= 0} className="btn btn-primary btn-lg w-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Lock className="h-4 w-4" aria-hidden="true" />}
          {busy ? 'Setting up your payment' : left <= 0 ? 'Session expired' : 'Pay securely'}
        </button>

        {error && (
          <button type="button" onClick={start} disabled={busy} className="btn btn-ghost btn-sm mt-2 w-full">
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            Try again
          </button>
        )}

        <button type="button" onClick={cancel} className="btn btn-ghost btn-sm mt-2 w-full text-content-faint">
          Cancel and go back
        </button>

        <p className="mt-5 text-center text-2xs leading-relaxed text-content-faint">
          Your card and UPI details are entered on your bank's own screen. RiskPay never sees them.
        </p>
      </div>
    </div>,
  );
}
